import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { PlayCircleIcon, FileText, ExternalLink } from 'lucide-react';
import ReactPlayer from 'react-player/youtube';
import { LessonSection as LessonSectionType } from '../data/courseData';
import { PerformanceGrid } from './PerformanceGrid';
import { PerformanceCalculator } from './PerformanceCalculator';
import { CareerTable } from './CareerTable';
import { CareerTimeline } from './CareerTimeline';
import { CareerCalculator } from './CareerCalculator';
import { ChecklistSection } from './ChecklistSection';
import { WorkflowSection } from './WorkflowSection';
import { TimelineProcess } from './TimelineProcess';
import { FlowchartSection } from './FlowchartSection';
import { ResourcesSection } from './ResourcesSection';
import { SupportAccordion } from './SupportAccordion';
import { CommunicationChecklist } from './CommunicationChecklist';
import { QuizSection } from './QuizSection';
import { CommissionTable } from './CommissionTable';
import { CommissionCalculator } from './CommissionCalculator';
import { ProspectingListTable } from './ProspectingListTable';
import { ProspectingWorkflow } from './ProspectingWorkflow';
import { StatusTable } from './StatusTable';
import { ConsequencesTable } from './ConsequencesTable';
import { SectionHeader } from './SectionHeader';
import { Accordion } from './Accordion';
import { AccordionItem } from './Accordion';
import { AccordionTrigger } from './Accordion';
import { AccordionContent } from './Accordion';
import { CheckCircleIcon } from 'lucide-react';
import { CalculatorIcon } from 'lucide-react';
interface LessonSectionProps {
  section: LessonSectionType;
  index: number;
}
// Helper function to get unique image for each section
const getSectionImage = (sectionId: string, sectionTitle: string): string => {
  // Map specific section IDs or titles to unique images
  const imageMap: Record<string, string> = {
    // Default/generic sections
    introducere: 'https://images.unsplash.com/photo-1552664730-d307ca884978?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    concept: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    structura: 'https://images.unsplash.com/photo-1497366216548-37526070297c?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    proces: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    echipa: 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    training: 'https://images.unsplash.com/photo-1524178232363-1fb2b075b655?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    suport: 'https://images.unsplash.com/photo-1556761175-b413da4baf72?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    performanta: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    cariera: 'https://images.unsplash.com/photo-1521737711867-e3b97375f902?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    dezvoltare: 'https://images.unsplash.com/photo-1552581234-26160f608093?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    comisioane: 'https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    contracte: 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    proprietar: 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    chirias: 'https://images.unsplash.com/photo-1582407947304-fd86f028f716?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    vizionare: 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    negociere: 'https://images.unsplash.com/photo-1556761175-4b46a572b786?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    finalizare: 'https://images.unsplash.com/photo-1521791136064-7986c2920216?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    documentatie: 'https://images.unsplash.com/photo-1568992687947-868a62a9f521?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    comunicare: 'https://images.unsplash.com/photo-1423666639041-f56000c27a9a?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    relationare: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    marketing: 'https://images.unsplash.com/photo-1533750349088-cd871a92f312?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    tehnologie: 'https://images.unsplash.com/photo-1519389950473-47ba0277781c?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    crm: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    platforma: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    instrumente: 'https://images.unsplash.com/photo-1484480974693-6ca0a78fb36b?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    resurse: 'https://images.unsplash.com/photo-1481627834876-b7833e8f5570?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    ghid: 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    proceduri: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    reguli: 'https://images.unsplash.com/photo-1589829545856-d10d557cf95f?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    etică: 'https://images.unsplash.com/photo-1521737711867-e3b97375f902?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    profesionalism: 'https://images.unsplash.com/photo-1507679799987-c73779587ccf?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    calitate: 'https://images.unsplash.com/photo-1552664730-d307ca884978?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    excelenta: 'https://images.unsplash.com/photo-1531482615713-2afd69097998?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    succes: 'https://images.unsplash.com/photo-1552664730-d307ca884978?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    rezultate: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80'
  };
  // Try to match by section ID first
  const idLower = sectionId.toLowerCase();
  for (const [key, url] of Object.entries(imageMap)) {
    if (idLower.includes(key)) {
      return url;
    }
  }
  // Try to match by section title
  const titleLower = sectionTitle.toLowerCase();
  for (const [key, url] of Object.entries(imageMap)) {
    if (titleLower.includes(key)) {
      return url;
    }
  }
  // Fallback: generate a unique image based on hash of section ID
  const hash = sectionId.split('').reduce((acc, char) => {
    return char.charCodeAt(0) + ((acc << 5) - acc);
  }, 0);
  const imageIds = ['photo-1552664730-d307ca884978', 'photo-1454165804606-c3d57bc86b40', 'photo-1497366216548-37526070297c', 'photo-1460925895917-afdab827c52f', 'photo-1522071820081-009f0129c71c', 'photo-1524178232363-1fb2b075b655', 'photo-1556761175-b413da4baf72', 'photo-1551288049-bebda4e38f71', 'photo-1521737711867-e3b97375f902', 'photo-1552581234-26160f608093', 'photo-1554224155-8d04cb21cd6c', 'photo-1450101499163-c8848c66ca85', 'photo-1560518883-ce09059eeffa', 'photo-1582407947304-fd86f028f716', 'photo-1423666639041-f56000c27a9a' // communication
  ];
  const imageId = imageIds[Math.abs(hash) % imageIds.length];
  return `https://images.unsplash.com/` + imageId + `?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80`;
};
export function LessonSection({
  section,
  index
}: LessonSectionProps) {
  if (section.type === 'performance-grid') {
    return <motion.section id={section.id} initial={{
      opacity: 0,
      y: 30
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true
    }} transition={{
      duration: 0.6,
      delay: index * 0.1
    }} className="my-16" data-id="element-1294">
        <h2 className="text-2xl md:text-3xl font-bold text-[#132C53] mb-6 flex items-center space-x-3" data-id="element-1295">
          <span data-id="element-1296">{section.emoji}</span>
          <span data-id="element-1297">{section.number}</span>
          <span data-id="element-1298">{section.title}</span>
        </h2>
        <p className="text-gray-700 text-lg mb-8" data-id="element-1299">{section.content}</p>
        <PerformanceGrid data-id="element-1300" />
      </motion.section>;
  }
  if (section.type === 'performance-calculator') {
    return <motion.section id={section.id} initial={{
      opacity: 0,
      y: 30
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true
    }} transition={{
      duration: 0.6,
      delay: index * 0.1
    }} className="my-16" data-id="element-1301">
        <h2 className="text-2xl md:text-3xl font-bold text-[#132C53] mb-6 flex items-center space-x-3" data-id="element-1302">
          <span data-id="element-1303">{section.emoji}</span>
          <span data-id="element-1304">{section.number}</span>
          <span data-id="element-1305">{section.title}</span>
        </h2>
        <p className="text-gray-700 text-lg mb-8" data-id="element-1306">{section.content}</p>
        <PerformanceCalculator data-id="element-1307" />
      </motion.section>;
  }
  if (section.type === 'career-table') {
    return <motion.section id={section.id} initial={{
      opacity: 0,
      y: 30
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true
    }} transition={{
      duration: 0.6,
      delay: index * 0.1
    }} className="my-16" data-id="element-1308">
        <h2 className="text-2xl md:text-3xl font-bold text-[#132C53] mb-6 flex items-center space-x-3" data-id="element-1309">
          <span data-id="element-1310">{section.emoji}</span>
          <span data-id="element-1311">{section.number}</span>
          <span data-id="element-1312">{section.title}</span>
        </h2>
        <p className="text-gray-700 text-lg mb-8" data-id="element-1313">{section.content}</p>
        {section.careerLevels && <CareerTable levels={section.careerLevels} data-id="element-1314" />}
      </motion.section>;
  }
  if (section.type === 'career-timeline') {
    return <motion.section id={section.id} initial={{
      opacity: 0,
      y: 30
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true
    }} transition={{
      duration: 0.6,
      delay: index * 0.1
    }} className="my-16" data-id="element-1315">
        <h2 className="text-2xl md:text-3xl font-bold text-[#132C53] mb-6 flex items-center space-x-3" data-id="element-1316">
          <span data-id="element-1317">{section.emoji}</span>
          <span data-id="element-1318">{section.number}</span>
          <span data-id="element-1319">{section.title}</span>
        </h2>
        <p className="text-gray-700 text-lg mb-8" data-id="element-1320">{section.content}</p>
        {section.timelineSteps && <CareerTimeline steps={section.timelineSteps} data-id="element-1321" />}
      </motion.section>;
  }
  if (section.type === 'career-calculator') {
    return <motion.section id={section.id} initial={{
      opacity: 0,
      y: 30
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true
    }} transition={{
      duration: 0.6,
      delay: index * 0.1
    }} className="my-16" data-id="element-1322">
        <h2 className="text-2xl md:text-3xl font-bold text-[#132C53] mb-6 flex items-center space-x-3" data-id="element-1323">
          <span data-id="element-1324">{section.emoji}</span>
          <span data-id="element-1325">{section.number}</span>
          <span data-id="element-1326">{section.title}</span>
        </h2>
        <p className="text-gray-700 text-lg mb-8" data-id="element-1327">{section.content}</p>
        <CareerCalculator data-id="element-1328" />
      </motion.section>;
  }
  if (section.type === 'video') {
    return <motion.section id={section.id} initial={{
      opacity: 0,
      y: 30
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true
    }} transition={{
      duration: 0.6,
      delay: index * 0.1
    }} className="my-16" data-id="element-1329"></motion.section>;
  }
  if (section.type === 'records') {
    return <motion.section id={section.id} initial={{
      opacity: 0,
      y: 30
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true
    }} transition={{
      duration: 0.6,
      delay: index * 0.1
    }} className="my-16" data-id="element-1330">
        <h2 className="text-2xl md:text-3xl font-bold text-[#132C53] mb-6 flex items-center space-x-3" data-id="element-1331">
          <span data-id="element-1332">{section.emoji}</span>
          <span data-id="element-1333">{section.number}</span>
          <span data-id="element-1334">{section.title}</span>
        </h2>
        <p className="text-gray-700 text-lg mb-8" data-id="element-1335">{section.content}</p>
        <div className="space-y-4" data-id="element-1336">
          {section.records?.map((record, i) => <motion.div key={i} initial={{
          opacity: 0,
          x: -20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.4,
          delay: i * 0.1
        }} className="bg-white rounded-xl p-6 shadow-md border-l-4 border-[#357BEB] hover:shadow-lg transition-shadow" data-id="element-1337">
              <div className="flex items-start space-x-4" data-id="element-1338">
                <div className="flex-shrink-0 w-12 h-12 bg-[#357BEB] bg-opacity-10 rounded-xl flex items-center justify-center text-2xl" data-id="element-1339">
                  {record.icon}
                </div>
                <div className="flex-1" data-id="element-1340">
                  <h3 className="text-lg font-bold text-[#132C53] mb-2" data-id="element-1341">
                    {record.category}
                  </h3>
                  <p className="text-[#357BEB] font-semibold mb-2" data-id="element-1342">
                    📊 {record.record}
                  </p>
                  <p className="text-gray-600 text-sm" data-id="element-1343">
                    🧭 {record.observation}
                  </p>
                </div>
              </div>
            </motion.div>)}
        </div>
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        delay: 0.3
      }} className="mt-8 bg-gradient-to-r from-[#357BEB] to-[#132C53] text-white p-6 rounded-xl text-center" data-id="element-1344">
          <p className="text-lg font-medium" data-id="element-1345">
            💡 Toate aceste rezultate au fost obținute în cadrul aceluiași
            sistem, fără excepție. Asta înseamnă că și tu poți ajunge acolo —
            dacă respecți procesul.
          </p>
        </motion.div>
      </motion.section>;
  }
  if (section.type === 'conclusion') {
    return <motion.section id={section.id} initial={{
      opacity: 0,
      y: 30
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true
    }} transition={{
      duration: 0.6,
      delay: index * 0.1
    }} className="my-16" data-id="element-1346">
        <div className="bg-gradient-to-r from-[#357BEB] to-[#132C53] rounded-2xl p-1" data-id="element-1347">
          <div className="bg-white rounded-xl p-8 md:p-12" data-id="element-1348">
            <h2 className="text-2xl md:text-3xl font-bold text-[#132C53] mb-6 flex items-center space-x-3" data-id="element-1349">
              <span data-id="element-1350">{section.emoji}</span>
              <span data-id="element-1351">{section.number}</span>
              <span data-id="element-1352">{section.title}</span>
            </h2>
            <div className="prose prose-lg max-w-none mb-8" data-id="element-1353">
              {section.content.split('\n\n').map((paragraph, i) => <p key={i} className="text-gray-700 leading-relaxed mb-4" data-id="element-1354">
                  {paragraph}
                </p>)}
            </div>
            {section.image && <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} className="relative rounded-xl overflow-hidden shadow-xl" data-id="element-1355">
                <img src="https://images.unsplash.com/photo-1552664730-d307ca884978?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80" alt={section.imageAlt} className="w-full h-64 object-cover" data-id="element-1356" />
              </motion.div>}
            <div className="mt-8 text-center" data-id="element-1357">
              <div className="inline-block bg-gradient-to-r from-[#357BEB] to-[#132C53] text-white px-8 py-3 rounded-full font-semibold" data-id="element-1358">
                ✅ Lecție finalizată! Continuă spre următoarea.
              </div>
            </div>
          </div>
        </div>
      </motion.section>;
  }
  if (section.type === 'checklist') {
    return <motion.section id={section.id} initial={{
      opacity: 0,
      y: 30
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true
    }} transition={{
      duration: 0.6,
      delay: index * 0.1
    }} className="my-16" data-id="element-1359">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start" data-id="element-1360">
          <div className="lg:col-span-7" data-id="element-1361">
            <h2 className="text-2xl md:text-3xl font-bold text-[#132C53] mb-6 flex items-center space-x-3" data-id="element-1362">
              <span data-id="element-1363">{section.emoji}</span>
              <span data-id="element-1364">{section.number}</span>
              <span data-id="element-1365">{section.title}</span>
            </h2>
            <div className="prose prose-lg max-w-none" data-id="element-1366">
              {section.content.split('\n\n').map((paragraph, i) => <p key={i} className="text-gray-700 leading-relaxed mb-4" data-id="element-1367">
                  {paragraph}
                </p>)}
            </div>
            {section.image && <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} className="mt-6 rounded-2xl overflow-hidden shadow-xl" data-id="element-1368">
                <img src="https://images.unsplash.com/photo-1484480974693-6ca0a78fb36b?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80" alt={section.imageAlt} className="w-full h-64 object-cover" data-id="element-1369" />
              </motion.div>}
          </div>
          <div className="lg:col-span-5" data-id="element-1370">
            <div className="sticky top-24" data-id="element-1371">
              <ChecklistSection data-id="element-1372" />
            </div>
          </div>
        </div>
      </motion.section>;
  }
  if (section.type === 'workflow') {
    return <motion.section id={section.id} initial={{
      opacity: 0,
      y: 30
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true
    }} transition={{
      duration: 0.6,
      delay: index * 0.1
    }} className="my-16" data-id="element-1373">
        <h2 className="text-2xl md:text-3xl font-bold text-[#132C53] mb-6 flex items-center space-x-3" data-id="element-1374">
          <span data-id="element-1375">{section.emoji}</span>
          <span data-id="element-1376">{section.number}</span>
          <span data-id="element-1377">{section.title}</span>
        </h2>
        <div className="prose prose-lg max-w-none mb-8" data-id="element-1378">
          {section.content.split('\n\n').map((paragraph, i) => {
          if (paragraph.startsWith('•')) {
            const items = paragraph.split('\n');
            return <ul key={i} className="space-y-3 mb-6" data-id="element-1379">
                  {items.map((item, j) => <li key={j} className="text-gray-700 leading-relaxed" data-id="element-1380">
                      {item.replace('• ', '')}
                    </li>)}
                </ul>;
          }
          return <p key={i} className="text-gray-700 leading-relaxed mb-4" data-id="element-1381">
                {paragraph}
              </p>;
        })}
        </div>
        <WorkflowSection data-id="element-1382" />
        {section.image && <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} whileInView={{
        opacity: 1,
        scale: 1
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5
      }} className="mt-8 rounded-2xl overflow-hidden shadow-xl" data-id="element-1383">
            <img src="https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80" alt={section.imageAlt} className="w-full h-64 object-cover" data-id="element-1384" />
          </motion.div>}
      </motion.section>;
  }
  if (section.type === 'timeline') {
    return <motion.section id={section.id} initial={{
      opacity: 0,
      y: 30
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true
    }} transition={{
      duration: 0.6,
      delay: index * 0.1
    }} className="my-16" data-id="element-1385">
        <h2 className="text-2xl md:text-3xl font-bold text-[#132C53] mb-6 flex items-center space-x-3" data-id="element-1386">
          <span data-id="element-1387">{section.emoji}</span>
          <span data-id="element-1388">{section.number}</span>
          <span data-id="element-1389">{section.title}</span>
        </h2>
        <p className="text-gray-700 text-lg mb-8" data-id="element-1390">{section.content}</p>
        <TimelineProcess data-id="element-1391" />
      </motion.section>;
  }
  if (section.type === 'flowchart') {
    return <motion.section id={section.id} initial={{
      opacity: 0,
      y: 30
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true
    }} transition={{
      duration: 0.6,
      delay: index * 0.1
    }} className="my-16" data-id="element-1392">
        <h2 className="text-2xl md:text-3xl font-bold text-[#132C53] mb-6 flex items-center space-x-3" data-id="element-1393">
          <span data-id="element-1394">{section.emoji}</span>
          <span data-id="element-1395">{section.number}</span>
          <span data-id="element-1396">{section.title}</span>
        </h2>
        <p className="text-gray-700 text-lg mb-8" data-id="element-1397">{section.content}</p>
        <FlowchartSection data-id="element-1398" />
      </motion.section>;
  }
  if (section.type === 'resources') {
    return;
  }
  if (section.type === 'support-accordion') {
    return <motion.section id={section.id} initial={{
      opacity: 0,
      y: 30
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true
    }} transition={{
      duration: 0.6,
      delay: index * 0.1
    }} className="my-16" data-id="element-1399">
        <h2 className="text-2xl md:text-3xl font-bold text-[#132C53] mb-6 flex items-center space-x-3" data-id="element-1400">
          <span data-id="element-1401">{section.emoji}</span>
          <span data-id="element-1402">{section.number}</span>
          <span data-id="element-1403">{section.title}</span>
        </h2>
        <p className="text-gray-700 text-lg mb-8" data-id="element-1404">{section.content}</p>
        <SupportAccordion data-id="element-1405" />
      </motion.section>;
  }
  if (section.type === 'communication-checklist') {
    return <motion.section id={section.id} initial={{
      opacity: 0,
      y: 30
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true
    }} transition={{
      duration: 0.6,
      delay: index * 0.1
    }} className="my-16" data-id="element-1406">
        <h2 className="text-2xl md:text-3xl font-bold text-[#132C53] mb-6 flex items-center space-x-3" data-id="element-1407">
          <span data-id="element-1408">{section.emoji}</span>
          <span data-id="element-1409">{section.number}</span>
          <span data-id="element-1410">{section.title}</span>
        </h2>
        <p className="text-gray-700 text-lg mb-8" data-id="element-1411">{section.content}</p>
        <CommunicationChecklist data-id="element-1412" />
      </motion.section>;
  }
  if (section.type === 'quiz') {
    return <motion.section id={section.id} initial={{
      opacity: 0,
      y: 30
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true
    }} transition={{
      duration: 0.6,
      delay: index * 0.1
    }} className="my-16" data-id="element-1413">
        <QuizSection data-id="element-1414" />
      </motion.section>;
  }
  if (section.type === 'commission-table') {
    return <motion.section id={section.id} initial={{
      opacity: 0,
      y: 30
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true
    }} transition={{
      duration: 0.6,
      delay: index * 0.1
    }} className="my-16" data-id="element-1415">
        <h2 className="text-2xl md:text-3xl font-bold text-[#132C53] mb-6 flex items-center space-x-3" data-id="element-1416">
          <span data-id="element-1417">{section.emoji}</span>
          <span data-id="element-1418">{section.number}</span>
          <span data-id="element-1419">{section.title}</span>
        </h2>
        <div className="prose prose-lg max-w-none mb-8" data-id="element-1420">
          {section.content.split('\n\n').map((paragraph, i) => <p key={i} className="text-gray-700 leading-relaxed mb-4" data-id="element-1421">
              {paragraph}
            </p>)}
        </div>
        <CommissionTable data-id="element-1422" />
      </motion.section>;
  }
  if (section.type === 'commission-calculator') {
    return <motion.section id={section.id} initial={{
      opacity: 0,
      y: 30
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true
    }} transition={{
      duration: 0.6,
      delay: index * 0.1
    }} className="my-16" data-id="element-1423">
        <h2 className="text-2xl md:text-3xl font-bold text-[#132C53] mb-6 flex items-center space-x-3" data-id="element-1424">
          <span data-id="element-1425">{section.emoji}</span>
          <span data-id="element-1426">{section.number}</span>
          <span data-id="element-1427">{section.title}</span>
        </h2>
        <p className="text-gray-700 text-lg mb-8" data-id="element-1428">{section.content}</p>
        <CommissionCalculator data-id="element-1429" />
      </motion.section>;
  }
  if (section.type === 'prospecting-list-table') {
    return <motion.section id={section.id} initial={{
      opacity: 0,
      y: 20
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true
    }} transition={{
      duration: 0.5,
      delay: index * 0.1
    }} className="mb-12 scroll-mt-24" data-id="element-1430">
        <h2 className="text-2xl font-bold text-[#132C53] mb-4 flex items-center space-x-2" data-id="element-1431">
          <span data-id="element-1432">{section.emoji}</span>
          <span data-id="element-1433">
            {section.number} {section.title}
          </span>
        </h2>
        <div className="prose prose-lg max-w-none mb-6" data-id="element-1434">
          <p className="text-gray-600 leading-relaxed" data-id="element-1435">{section.content}</p>
        </div>
        {/* Prospecting List Table */}
        <div className="overflow-x-auto bg-white rounded-lg shadow-md border border-gray-200" data-id="element-1436">
          <table className="min-w-full divide-y divide-gray-200" data-id="element-1437">
            <thead className="bg-gradient-to-r from-[#132C53] to-[#1e4278]" data-id="element-1438">
              <tr data-id="element-1439">
                <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider" data-id="element-1440">
                  Zonă
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider" data-id="element-1441">
                  Nr. Camere
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider" data-id="element-1442">
                  Suprafață
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider" data-id="element-1443">
                  Etaj
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider" data-id="element-1444">
                  Preț
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider" data-id="element-1445">
                  Telefon
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider" data-id="element-1446">
                  Sursă
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider" data-id="element-1447">
                  Verificare CRM
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider" data-id="element-1448">
                  Apel
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider" data-id="element-1449">
                  Colaborare Preluată
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider" data-id="element-1450">
                  ID CRM
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-1451">
              <tr className="hover:bg-gray-50" data-id="element-1452">
                <td className="px-4 py-3 text-sm text-gray-700" data-id="element-1453">Floreasca</td>
                <td className="px-4 py-3 text-sm text-gray-700" data-id="element-1454">2</td>
                <td className="px-4 py-3 text-sm text-gray-700" data-id="element-1455">65 mp</td>
                <td className="px-4 py-3 text-sm text-gray-700" data-id="element-1456">3/8</td>
                <td className="px-4 py-3 text-sm text-gray-700" data-id="element-1457">850 €</td>
                <td className="px-4 py-3 text-sm text-gray-700" data-id="element-1458">
                  0721 XXX XXX
                </td>
                <td className="px-4 py-3 text-sm text-gray-700" data-id="element-1459">
                  Imobiliare.ro
                </td>
                <td className="px-4 py-3 text-sm" data-id="element-1460">
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800" data-id="element-1461">
                    Verifică
                  </span>
                </td>
                <td className="px-4 py-3 text-sm" data-id="element-1462">
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800" data-id="element-1463">
                    Apelează
                  </span>
                </td>
                <td className="px-4 py-3 text-sm text-center" data-id="element-1464">—</td>
                <td className="px-4 py-3 text-sm text-center" data-id="element-1465">—</td>
              </tr>
              <tr className="hover:bg-gray-50" data-id="element-1466">
                <td className="px-4 py-3 text-sm text-gray-700" data-id="element-1467">Pipera</td>
                <td className="px-4 py-3 text-sm text-gray-700" data-id="element-1468">3</td>
                <td className="px-4 py-3 text-sm text-gray-700" data-id="element-1469">90 mp</td>
                <td className="px-4 py-3 text-sm text-gray-700" data-id="element-1470">2/4</td>
                <td className="px-4 py-3 text-sm text-gray-700" data-id="element-1471">1.200 €</td>
                <td className="px-4 py-3 text-sm text-gray-700" data-id="element-1472">
                  0722 XXX XXX
                </td>
                <td className="px-4 py-3 text-sm text-gray-700" data-id="element-1473">OLX</td>
                <td className="px-4 py-3 text-sm" data-id="element-1474">
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-1475">
                    ✓ Verificat
                  </span>
                </td>
                <td className="px-4 py-3 text-sm" data-id="element-1476">
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-1477">
                    Colaborează
                  </span>
                </td>
                <td className="px-4 py-3 text-sm text-center" data-id="element-1478">
                  <CheckCircleIcon className="w-5 h-5 text-green-600 mx-auto" data-id="element-1479" />
                </td>
                <td className="px-4 py-3 text-sm font-mono text-[#357BEB]" data-id="element-1480">
                  P14433
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div className="mt-4 flex items-center justify-between text-sm" data-id="element-1481">
          <p className="text-gray-600" data-id="element-1482">
            <strong data-id="element-1483">Exemplu:</strong> ID CRM de forma P14433 generat la
            indexare.
          </p>
          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800" data-id="element-1484">
            Tip date: UI demo • fără backend
          </span>
        </div>
        {section.image && <div className="mt-6" data-id="element-1485">
            <img src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80" alt={section.imageAlt || section.title} className="w-full rounded-lg shadow-md" data-id="element-1486" />
          </div>}
      </motion.section>;
  }
  if (section.type === 'prospecting-workflow') {
    return <motion.section id={section.id} initial={{
      opacity: 0,
      y: 20
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true
    }} transition={{
      duration: 0.5,
      delay: index * 0.1
    }} className="mb-12 scroll-mt-24" data-id="element-1487">
        <h2 className="text-2xl font-bold text-[#132C53] mb-4 flex items-center space-x-2" data-id="element-1488">
          <span data-id="element-1489">{section.emoji}</span>
          <span data-id="element-1490">
            {section.number} {section.title}
          </span>
        </h2>
        <div className="prose prose-lg max-w-none mb-6" data-id="element-1491">
          <p className="text-gray-600 leading-relaxed" data-id="element-1492">{section.content}</p>
        </div>
        {/* Workflow Steps */}
        <div className="space-y-4" data-id="element-1493">
          <div className="bg-white rounded-lg p-6 shadow-md border-l-4 border-[#357BEB]" data-id="element-1494">
            <div className="flex items-start space-x-4" data-id="element-1495">
              <div className="flex-shrink-0 w-10 h-10 bg-[#357BEB] text-white rounded-full flex items-center justify-center font-bold" data-id="element-1496">
                1
              </div>
              <div className="flex-1" data-id="element-1497">
                <h3 className="font-bold text-[#132C53] mb-2" data-id="element-1498">
                  Accesează lista personală
                </h3>
                <p className="text-gray-600" data-id="element-1499">
                  Deschide Google Sheet-ul alocat ție pentru ziua respectivă.
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg p-6 shadow-md border-l-4 border-[#357BEB]" data-id="element-1500">
            <div className="flex items-start space-x-4" data-id="element-1501">
              <div className="flex-shrink-0 w-10 h-10 bg-[#357BEB] text-white rounded-full flex items-center justify-center font-bold" data-id="element-1502">
                2
              </div>
              <div className="flex-1" data-id="element-1503">
                <h3 className="font-bold text-[#132C53] mb-2" data-id="element-1504">
                  Verifică în CRM
                </h3>
                <p className="text-gray-600 mb-2" data-id="element-1505">
                  Caută după număr de telefon în CRM:
                </p>
                <ul className="list-disc list-inside text-gray-600 space-y-1" data-id="element-1506">
                  <li data-id="element-1507">
                    Dacă există colaborare activă (status roșu) → NU contacta
                  </li>
                  <li data-id="element-1508">Altfel → lead disponibil pentru apel</li>
                </ul>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg p-6 shadow-md border-l-4 border-[#357BEB]" data-id="element-1509">
            <div className="flex items-start space-x-4" data-id="element-1510">
              <div className="flex-shrink-0 w-10 h-10 bg-[#357BEB] text-white rounded-full flex items-center justify-center font-bold" data-id="element-1511">
                3
              </div>
              <div className="flex-1" data-id="element-1512">
                <h3 className="font-bold text-[#132C53] mb-2" data-id="element-1513">
                  Efectuează apelul
                </h3>
                <p className="text-gray-600" data-id="element-1514">
                  Urmează scriptul standard de prospectare învățat în lecția
                  anterioară.
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg p-6 shadow-md border-l-4 border-[#357BEB]" data-id="element-1515">
            <div className="flex items-start space-x-4" data-id="element-1516">
              <div className="flex-shrink-0 w-10 h-10 bg-[#357BEB] text-white rounded-full flex items-center justify-center font-bold" data-id="element-1517">
                4
              </div>
              <div className="flex-1" data-id="element-1518">
                <h3 className="font-bold text-[#132C53] mb-2" data-id="element-1519">
                  Completează statusul
                </h3>
                <p className="text-gray-600" data-id="element-1520">
                  Introdu în coloana "Apel" statusul corect (vezi secțiunea
                  următoare pentru toate statusurile disponibile).
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg p-6 shadow-md border-l-4 border-[#21A366]" data-id="element-1521">
            <div className="flex items-start space-x-4" data-id="element-1522">
              <div className="flex-shrink-0 w-10 h-10 bg-[#21A366] text-white rounded-full flex items-center justify-center font-bold" data-id="element-1523">
                5
              </div>
              <div className="flex-1" data-id="element-1524">
                <h3 className="font-bold text-[#132C53] mb-2" data-id="element-1525">
                  Dacă proprietarul colaborează
                </h3>
                <ul className="list-disc list-inside text-gray-600 space-y-1" data-id="element-1526">
                  <li data-id="element-1527">Indexează proprietatea în CRM</li>
                  <li data-id="element-1528">Copiază ID-ul generat (ex: P9211)</li>
                  <li data-id="element-1529">Inserează ID-ul în coloana "ID CRM"</li>
                  <li data-id="element-1530">Bifează "Colaborare Preluată"</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
        <div className="mt-6 bg-blue-50 border-l-4 border-[#357BEB] p-6 rounded-r-lg" data-id="element-1531">
          <p className="text-[#132C53] font-medium" data-id="element-1532">
            💡 Profesionalismul începe cu pregătirea și se validează prin
            completare corectă.
          </p>
        </div>
      </motion.section>;
  }
  if (section.type === 'status-table') {
    return <motion.section id={section.id} initial={{
      opacity: 0,
      y: 20
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true
    }} transition={{
      duration: 0.5,
      delay: index * 0.1
    }} className="mb-12 scroll-mt-24" data-id="element-1533">
        <h2 className="text-2xl font-bold text-[#132C53] mb-4 flex items-center space-x-2" data-id="element-1534">
          <span data-id="element-1535">{section.emoji}</span>
          <span data-id="element-1536">
            {section.number} {section.title}
          </span>
        </h2>
        <div className="prose prose-lg max-w-none mb-6" data-id="element-1537">
          <p className="text-gray-600 leading-relaxed" data-id="element-1538">{section.content}</p>
        </div>
        {/* Status Badges Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1539">
          <div className="bg-white rounded-lg p-4 shadow-md border-l-4 border-red-500" data-id="element-1540">
            <div className="flex items-center space-x-3 mb-2" data-id="element-1541">
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800" data-id="element-1542">
                Nu colaborează
              </span>
            </div>
            <p className="text-sm text-gray-600" data-id="element-1543">
              Proprietarul refuză colaborarea.
            </p>
          </div>
          <div className="bg-white rounded-lg p-4 shadow-md border-l-4 border-green-500" data-id="element-1544">
            <div className="flex items-center space-x-3 mb-2" data-id="element-1545">
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800" data-id="element-1546">
                Colaborează
              </span>
              <span className="text-xs text-red-600 font-semibold" data-id="element-1547">
                ⚑ necesită ID CRM
              </span>
            </div>
            <p className="text-sm text-gray-600" data-id="element-1548">
              Proprietarul acceptă colaborarea.
            </p>
          </div>
          <div className="bg-white rounded-lg p-4 shadow-md border-l-4 border-yellow-500" data-id="element-1549">
            <div className="flex items-center space-x-3 mb-2" data-id="element-1550">
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-yellow-100 text-yellow-800" data-id="element-1551">
                Nu răspunde / mesaj trimis
              </span>
            </div>
            <p className="text-sm text-gray-600" data-id="element-1552">Reapelare ulterioară.</p>
          </div>
          <div className="bg-white rounded-lg p-4 shadow-md border-l-4 border-gray-500" data-id="element-1553">
            <div className="flex items-center space-x-3 mb-2" data-id="element-1554">
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-800" data-id="element-1555">
                Număr invalid
              </span>
            </div>
            <p className="text-sm text-gray-600" data-id="element-1556">
              Telefon incorect / inexistent.
            </p>
          </div>
          <div className="bg-white rounded-lg p-4 shadow-md border-l-4 border-blue-500" data-id="element-1557">
            <div className="flex items-center space-x-3 mb-2" data-id="element-1558">
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800" data-id="element-1559">
                A închiriat deja
              </span>
            </div>
            <p className="text-sm text-gray-600" data-id="element-1560">Proprietatea ocupată.</p>
          </div>
          <div className="bg-white rounded-lg p-4 shadow-md border-l-4 border-purple-500" data-id="element-1561">
            <div className="flex items-center space-x-3 mb-2" data-id="element-1562">
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-purple-100 text-purple-800" data-id="element-1563">
                Dorește vânzare
              </span>
            </div>
            <p className="text-sm text-gray-600" data-id="element-1564">
              Redirecționare la segment vânzare.
            </p>
          </div>
          <div className="bg-white rounded-lg p-4 shadow-md border-l-4 border-indigo-500" data-id="element-1565">
            <div className="flex items-center space-x-3 mb-2" data-id="element-1566">
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-indigo-100 text-indigo-800" data-id="element-1567">
                Colaborare coleg CLC
              </span>
            </div>
            <p className="text-sm text-gray-600" data-id="element-1568">
              Gestionat de alt agent din echipă.
            </p>
          </div>
          <div className="bg-white rounded-lg p-4 shadow-md border-l-4 border-orange-500" data-id="element-1569">
            <div className="flex items-center space-x-3 mb-2" data-id="element-1570">
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-orange-100 text-orange-800" data-id="element-1571">
                Oferă comision redus
              </span>
            </div>
            <p className="text-sm text-gray-600" data-id="element-1572">
              Se cere derogare în grupul de suport.
            </p>
          </div>
        </div>
        <div className="mt-6 bg-blue-50 border-l-4 border-[#357BEB] p-6 rounded-r-lg" data-id="element-1573">
          <p className="text-[#132C53] font-medium" data-id="element-1574">
            🧠 Lead finalizat = Status "Colaborează" + ID CRM completat.
          </p>
        </div>
      </motion.section>;
  }
  if (section.type === 'consequences-table') {
    return <motion.section id={section.id} initial={{
      opacity: 0,
      y: 20
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true
    }} transition={{
      duration: 0.5,
      delay: index * 0.1
    }} className="mb-12 scroll-mt-24" data-id="element-1575">
        <h2 className="text-2xl font-bold text-[#132C53] mb-4 flex items-center space-x-2" data-id="element-1576">
          <span data-id="element-1577">{section.emoji}</span>
          <span data-id="element-1578">
            {section.number} {section.title}
          </span>
        </h2>
        <div className="prose prose-lg max-w-none mb-6" data-id="element-1579">
          <p className="text-gray-600 leading-relaxed" data-id="element-1580">{section.content}</p>
        </div>
        {/* Consequences Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1581">
          <div className="bg-white rounded-lg p-6 shadow-md border-t-4 border-yellow-500" data-id="element-1582">
            <h3 className="font-bold text-[#132C53] mb-2 flex items-center space-x-2" data-id="element-1583">
              <span className="text-2xl" data-id="element-1584">⚠️</span>
              <span data-id="element-1585">Situație</span>
            </h3>
            <p className="text-gray-700 mb-3" data-id="element-1586">
              Neprelucrează lista zilnică fără motiv
            </p>
            <div className="bg-yellow-50 p-3 rounded-lg" data-id="element-1587">
              <p className="text-sm font-semibold text-yellow-800" data-id="element-1588">
                Consecință:
              </p>
              <p className="text-sm text-gray-700" data-id="element-1589">
                Blocare generare listă nouă.
              </p>
            </div>
          </div>
          <div className="bg-white rounded-lg p-6 shadow-md border-t-4 border-orange-500" data-id="element-1590">
            <h3 className="font-bold text-[#132C53] mb-2 flex items-center space-x-2" data-id="element-1591">
              <span className="text-2xl" data-id="element-1592">🚫</span>
              <span data-id="element-1593">Situație</span>
            </h3>
            <p className="text-gray-700 mb-3" data-id="element-1594">
              Neprelucrează consecutiv (2+ zile)
            </p>
            <div className="bg-orange-50 p-3 rounded-lg" data-id="element-1595">
              <p className="text-sm font-semibold text-orange-800" data-id="element-1596">
                Consecință:
              </p>
              <p className="text-sm text-gray-700" data-id="element-1597">
                Blocare cont și audit de performanță.
              </p>
            </div>
          </div>
          <div className="bg-white rounded-lg p-6 shadow-md border-t-4 border-red-500" data-id="element-1598">
            <h3 className="font-bold text-[#132C53] mb-2 flex items-center space-x-2" data-id="element-1599">
              <span className="text-2xl" data-id="element-1600">📉</span>
              <span data-id="element-1601">Situație</span>
            </h3>
            <p className="text-gray-700 mb-3" data-id="element-1602">
              Conversie sub 40% timp de 7 zile
            </p>
            <div className="bg-red-50 p-3 rounded-lg" data-id="element-1603">
              <p className="text-sm font-semibold text-red-800" data-id="element-1604">Consecință:</p>
              <p className="text-sm text-gray-700" data-id="element-1605">
                Sesiune de evaluare și re-training obligatoriu.
              </p>
            </div>
          </div>
          <div className="bg-white rounded-lg p-6 shadow-md border-t-4 border-gray-500" data-id="element-1606">
            <h3 className="font-bold text-[#132C53] mb-2 flex items-center space-x-2" data-id="element-1607">
              <span className="text-2xl" data-id="element-1608">💬</span>
              <span data-id="element-1609">Situație</span>
            </h3>
            <p className="text-gray-700 mb-3" data-id="element-1610">
              Lipsă comunicare cu suportul la probleme
            </p>
            <div className="bg-gray-50 p-3 rounded-lg" data-id="element-1611">
              <p className="text-sm font-semibold text-gray-800" data-id="element-1612">Consecință:</p>
              <p className="text-sm text-gray-700" data-id="element-1613">
                Avertisment și raportare către manager.
              </p>
            </div>
          </div>
        </div>
      </motion.section>;
  }
  if (section.type === 'call-preparation-checklist') {
    return <motion.section id={section.id} initial={{
      opacity: 0,
      y: 20
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true
    }} transition={{
      duration: 0.5,
      delay: index * 0.1
    }} className="mb-12 scroll-mt-24" data-id="element-1614">
        <SectionHeader section={section} data-id="element-1615" />
        <div className="space-y-4" data-id="element-1616">
          {[{
          number: '1️⃣',
          title: 'Vizualizezi complet anunțul proprietarului',
          description: 'Text, poze, preț, zonă, tip locuință'
        }, {
          number: '2️⃣',
          title: 'Notezi detalii importante',
          description: 'Etaj, suprafață, dotări, disponibilitate, preț cerut'
        }, {
          number: '3️⃣',
          title: 'Verifici dacă proprietatea există în CRM',
          description: 'Caută numărul de telefon în sistem'
        }, {
          number: '4️⃣',
          title: 'Pregătești o fișă de apel',
          description: 'Unde vei nota toate informațiile obținute'
        }].map((step, idx) => <motion.div key={idx} initial={{
          opacity: 0,
          x: -20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.3,
          delay: idx * 0.1
        }} className="bg-white rounded-xl p-6 shadow-md border-l-4 border-[#357BEB]" data-id="element-1617">
              <div className="flex items-start space-x-4" data-id="element-1618">
                <div className="flex-shrink-0 w-10 h-10 bg-[#357BEB] rounded-full flex items-center justify-center text-white font-bold" data-id="element-1619">
                  {step.number}
                </div>
                <div className="flex-1" data-id="element-1620">
                  <h4 className="font-bold text-[#132C53] mb-1" data-id="element-1621">
                    {step.title}
                  </h4>
                  <p className="text-gray-600 text-sm" data-id="element-1622">{step.description}</p>
                </div>
              </div>
            </motion.div>)}
        </div>
        <div className="mt-6 bg-blue-50 border-l-4 border-[#357BEB] p-4 rounded-r-lg" data-id="element-1623">
          <p className="text-[#132C53] text-sm italic" data-id="element-1624">
            💡 Profesionalismul nu începe în apel – începe cu pregătirea.
          </p>
        </div>
        {section.image && <motion.img initial={{
        opacity: 0,
        scale: 0.95
      }} whileInView={{
        opacity: 1,
        scale: 1
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5
      }} src="https://images.unsplash.com/photo-1423666639041-f56000c27a9a?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80" alt={section.imageAlt || ''} className="w-full rounded-xl shadow-lg mt-6" data-id="element-1625" />}
      </motion.section>;
  }
  if (section.type === 'call-script-accordion') {
    const scriptSections = [{
      title: '🔹 INTRODUCERE – NATURALĂ ȘI CALDĂ',
      content: '"Bună ziua! Mă numesc [Numele tău] și vă sun în legătură cu apartamentul dvs. de închiriat din zona ___________. Ce faceți, v-am prins într-un moment bun?"'
    }, {
      title: '🔹 CÂRLIG – DE CE SUNĂM (FĂRĂ PRESIUNE)',
      content: '"O să fiu foarte direct, nu vreau să vă rețin prea mult.\n\nȘtiu că ați pus un anunț și probabil încercați să închiriați pe cont propriu – și e absolut firesc, doar că voiam să știu dacă aveți nevoie de ajutor în vederea închirierii, pentru că nu v-am sunat la întâmplare.\n\nAgenția noastră a avut mai multe vizionări în zona dvs., a reușit să închirieze și să găsească imobile pentru o parte din clienții noștri, iar cum bine știți, un apartament se vizionează de mai mulți clienți, dar doar unul poate să-l închirieze, iar ceilalți rămân activi în căutare.\n\nIar imobilul dvs. se potrivește perfect cu cerințele câtorva dintre ei.\n\nAșa că am vrut să vă întreb simplu: aveți nevoie de ajutor sau ați fi deschis(ă) să colaborăm punctual – în sensul că, dacă am client interesat, să vă pot contacta și să programăm o vizionare?"'
    }, {
      title: '🔹 DACĂ E DESCHIS(Ă)',
      content: '"Super, mă bucur! Important de știut – dacă închiriem printr-un client adus de noi, comisionul este 50% + TVA (21%) din prima lună de chirie. Se plătește doar dacă se semnează contractul de închiriere.\n\nPerfect, să știți că nu vă cerem acum niciun contract. Înainte să ajung cu clientul la prima vizionare, ne vedem pentru a finaliza și semna contractul de intermediere – este foarte simplu, are o pagină.\n\nScopul nostru e să fim o opțiune complementară, nu o presiune. Iar dacă închiriați pe cont propriu între timp, cu atât mai bine."'
    }, {
      title: '🔹 INFORMAȚII DE CARE AVEM NEVOIE',
      content: '"Tocmai ca să nu pierdem timpul nimănui și să nu trimitem clienți nepotriviți, mă ajută să-mi spuneți câteva lucruri în plus față de ce e în anunț:\n\n• Care este disponibilitatea vizionărilor?\n• E disponibil acum sau de la o anumită dată?\n• Cam care este costul de întreținere?\n• Are loc de parcare?\n• Are centrală proprie sau termoficare?\n• Acceptați animale?\n• Ce tip de chiriaș preferați – familie, single, studenți?"'
    }, {
      title: '🔹 ÎNCHEIERE – NATURALĂ ȘI PRIETENOASĂ',
      content: '"Mulțumesc mult pentru timpul acordat! Vă țin la curent imediat ce transmit detaliile către clienții noștri."'
    }];
    return <motion.section id={section.id} initial={{
      opacity: 0,
      y: 20
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true
    }} transition={{
      duration: 0.5,
      delay: index * 0.1
    }} className="mb-12 scroll-mt-24" data-id="element-1626">
        <SectionHeader section={section} data-id="element-1627" />
        <div className="bg-gradient-to-br from-[#132C53] to-[#1e4278] rounded-xl p-6 mb-6" data-id="element-1628">
          <h3 className="text-xl font-bold text-white mb-2" data-id="element-1629">
            📄 SCRIPT CLC – Apel Proprietar Închiriere
          </h3>
          <p className="text-gray-300 text-sm" data-id="element-1630">
            Versiune conversațională & umană
          </p>
        </div>
        <div className="space-y-3" data-id="element-1631">
          {scriptSections.map((scriptSection, idx) => <Accordion key={idx} type="single" collapsible data-id="element-1632">
              <AccordionItem value={`script-${idx}`} className="bg-white rounded-xl shadow-md border border-gray-200" data-id="element-1633">
                <AccordionTrigger className="px-6 py-4 hover:no-underline" data-id="element-1634">
                  <span className="font-semibold text-[#132C53] text-left" data-id="element-1635">
                    {scriptSection.title}
                  </span>
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-4" data-id="element-1636">
                  <div className="bg-gray-50 rounded-lg p-4 whitespace-pre-line" data-id="element-1637">
                    <p className="text-gray-700 leading-relaxed" data-id="element-1638">
                      {scriptSection.content}
                    </p>
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>)}
        </div>
        <div className="mt-6 bg-gradient-to-r from-green-50 to-green-100 border-l-4 border-[#21A366] p-6 rounded-r-xl" data-id="element-1639">
          <h4 className="font-bold text-[#132C53] mb-3 flex items-center space-x-2" data-id="element-1640">
            <span data-id="element-1641">💬</span>
            <span data-id="element-1642">TON FINAL PENTRU AGENT CLC</span>
          </h4>
          <ul className="space-y-2 text-gray-700" data-id="element-1643">
            <li className="flex items-start space-x-2" data-id="element-1644">
              <span className="text-[#21A366] mt-1" data-id="element-1645">✓</span>
              <span data-id="element-1646">Nu "vinzi serviciul", vinzi soluția la un blocaj.</span>
            </li>
            <li className="flex items-start space-x-2" data-id="element-1647">
              <span className="text-[#21A366] mt-1" data-id="element-1648">✓</span>
              <span data-id="element-1649">Fii natural, vorbește ca între oameni.</span>
            </li>
            <li className="flex items-start space-x-2" data-id="element-1650">
              <span className="text-[#21A366] mt-1" data-id="element-1651">✓</span>
              <span data-id="element-1652">
                Nu păși în zona de negociere decât dacă întreabă proprietarul.
              </span>
            </li>
            <li className="flex items-start space-x-2" data-id="element-1653">
              <span className="text-[#21A366] mt-1" data-id="element-1654">✓</span>
              <span data-id="element-1655">Arată că vii cu clienți reali, nu cu promisiuni.</span>
            </li>
          </ul>
        </div>
      </motion.section>;
  }
  if (section.type === 'call-rules-grid') {
    const rules = [{
      icon: '🎯',
      title: 'Fii calm, politicos și direct',
      description: 'Tonul tău stabilește încrederea'
    }, {
      icon: '🤝',
      title: 'Nu forța colaborarea',
      description: 'Urmărește încrederea, nu vânzarea'
    }, {
      icon: '📝',
      title: 'Notează tot în CRM',
      description: 'Fiecare detaliu contează'
    }, {
      icon: '📋',
      title: 'Respectă exact scriptul',
      description: 'Este testat și optimizat'
    }, {
      icon: '💶',
      title: 'Nu discuta comisioane suplimentare',
      description: 'Doar standardul agenției'
    }];
    return <motion.section id={section.id} initial={{
      opacity: 0,
      y: 20
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true
    }} transition={{
      duration: 0.5,
      delay: index * 0.1
    }} className="mb-12 scroll-mt-24" data-id="element-1656">
        <SectionHeader section={section} data-id="element-1657" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-1658">
          {rules.map((rule, idx) => <motion.div key={idx} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.3,
          delay: idx * 0.1
        }} className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-all border-2 border-gray-100 hover:border-[#357BEB]" data-id="element-1659">
              <div className="text-4xl mb-3" data-id="element-1660">{rule.icon}</div>
              <h4 className="font-bold text-[#132C53] mb-2" data-id="element-1661">{rule.title}</h4>
              <p className="text-gray-600 text-sm" data-id="element-1662">{rule.description}</p>
            </motion.div>)}
        </div>
      </motion.section>;
  }
  if (section.type === 'tenant-commission-formula') {
    return <motion.section id={section.id} initial={{
      opacity: 0,
      y: 20
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true
    }} transition={{
      duration: 0.5,
      delay: index * 0.1
    }} className="mb-12 scroll-mt-24" data-id="element-1663">
        <h2 className="text-2xl font-bold text-[#132C53] mb-4 flex items-center space-x-2" data-id="element-1664">
          <span data-id="element-1665">{section.emoji}</span>
          <span data-id="element-1666">
            {section.number} {section.title}
          </span>
        </h2>
        <div className="prose prose-lg max-w-none mb-6" data-id="element-1667">
          <p className="text-gray-600 leading-relaxed" data-id="element-1668">{section.content}</p>
        </div>
        {/* Formula Card */}
        <div className="bg-gradient-to-r from-[#132C53] to-[#1e4278] text-white rounded-lg p-8 shadow-lg mb-6" data-id="element-1669">
          <div className="text-center mb-6" data-id="element-1670">
            <div className="text-3xl font-bold mb-4" data-id="element-1671">
              Comision<sub data-id="element-1672">Chiriaș</sub> = 50% × Valoarea chiriei lunare + TVA
              (21%)
            </div>
          </div>
          <div className="bg-white/10 rounded-lg p-6 backdrop-blur-sm" data-id="element-1673">
            <h3 className="font-bold text-xl mb-4 text-center" data-id="element-1674">
              Exemplu de calcul:
            </h3>
            <div className="space-y-3 text-lg" data-id="element-1675">
              <div className="flex justify-between items-center" data-id="element-1676">
                <span data-id="element-1677">Dacă chiria negociată este:</span>
                <span className="font-bold text-[#21A366]" data-id="element-1678">600 €</span>
              </div>
              <div className="border-t border-white/20 pt-3" data-id="element-1679">
                <div className="flex justify-between items-center mb-2" data-id="element-1680">
                  <span data-id="element-1681">→ Comision fără TVA:</span>
                  <span className="font-bold" data-id="element-1682">300 €</span>
                </div>
                <div className="flex justify-between items-center mb-2" data-id="element-1683">
                  <span data-id="element-1684">→ TVA (21%):</span>
                  <span className="font-bold" data-id="element-1685">63 €</span>
                </div>
                <div className="flex justify-between items-center text-xl border-t border-white/20 pt-3 mt-3" data-id="element-1686">
                  <span className="font-bold" data-id="element-1687">Total:</span>
                  <span className="font-bold text-[#21A366]" data-id="element-1688">363 €</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="bg-blue-50 border-l-4 border-[#357BEB] p-6 rounded-r-lg" data-id="element-1689">
          <p className="text-[#132C53] font-medium" data-id="element-1690">
            💡 TVA-ul se aplică la valoarea comisionului, iar factura se emite
            doar după semnarea contractului.
          </p>
        </div>
      </motion.section>;
  }
  if (section.type === 'tenant-commission-calculator') {
    const [rentValue, setRentValue] = useState<string>('');
    const [result, setResult] = useState<{
      baseCommission: number;
      vat: number;
      total: number;
    } | null>(null);
    const calculateCommission = () => {
      const rent = parseFloat(rentValue);
      if (isNaN(rent) || rent <= 0) {
        alert('Te rugăm să introduci o valoare validă pentru chirie.');
        return;
      }
      const baseCommission = rent * 0.5;
      const vat = baseCommission * 0.21;
      const total = baseCommission + vat;
      setResult({
        baseCommission: Math.round(baseCommission * 100) / 100,
        vat: Math.round(vat * 100) / 100,
        total: Math.round(total * 100) / 100
      });
    };
    return <motion.section id={section.id} initial={{
      opacity: 0,
      y: 20
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true
    }} transition={{
      duration: 0.5,
      delay: index * 0.1
    }} className="mb-12 scroll-mt-24" data-id="element-1691">
        <h2 className="text-2xl font-bold text-[#132C53] mb-4 flex items-center space-x-2" data-id="element-1692">
          <span data-id="element-1693">{section.emoji}</span>
          <span data-id="element-1694">
            {section.number} {section.title}
          </span>
        </h2>
        <div className="prose prose-lg max-w-none mb-6" data-id="element-1695">
          <p className="text-gray-600 leading-relaxed" data-id="element-1696">{section.content}</p>
        </div>
        {/* Calculator Card */}
        <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg p-8 shadow-md border border-gray-200" data-id="element-1697">
          <div className="flex items-center justify-center mb-6" data-id="element-1698">
            <CalculatorIcon className="w-12 h-12 text-[#357BEB]" data-id="element-1699" />
          </div>
          <div className="max-w-md mx-auto space-y-6" data-id="element-1700">
            <div data-id="element-1701">
              <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1702">
                Introdu valoarea chiriei lunare (€)
              </label>
              <input type="number" value={rentValue} onChange={e => setRentValue(e.target.value)} placeholder="ex: 600" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#357BEB] focus:border-transparent" data-id="element-1703" />
            </div>
            <button onClick={calculateCommission} className="w-full bg-[#357BEB] hover:bg-[#2868d8] text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-200 shadow-md" data-id="element-1704">
              Calculează Comision
            </button>
            {result && <motion.div initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} className="bg-white rounded-lg p-6 shadow-md border-2 border-[#21A366]" data-id="element-1705">
                <h3 className="font-bold text-lg text-[#132C53] mb-4 text-center" data-id="element-1706">
                  Rezultat calcul:
                </h3>
                <div className="space-y-3" data-id="element-1707">
                  <div className="flex justify-between items-center pb-2 border-b border-gray-200" data-id="element-1708">
                    <span className="text-gray-600" data-id="element-1709">Comision fără TVA:</span>
                    <span className="font-bold text-[#132C53]" data-id="element-1710">
                      {result.baseCommission} €
                    </span>
                  </div>
                  <div className="flex justify-between items-center pb-2 border-b border-gray-200" data-id="element-1711">
                    <span className="text-gray-600" data-id="element-1712">TVA (21%):</span>
                    <span className="font-bold text-[#132C53]" data-id="element-1713">
                      {result.vat} €
                    </span>
                  </div>
                  <div className="flex justify-between items-center pt-2" data-id="element-1714">
                    <span className="font-bold text-lg text-[#132C53]" data-id="element-1715">
                      Total:
                    </span>
                    <span className="font-bold text-2xl text-[#21A366]" data-id="element-1716">
                      {result.total} €
                    </span>
                  </div>
                </div>
              </motion.div>}
          </div>
        </div>
      </motion.section>;
  }
  if (section.type === 'billing-timeline') {
    return <motion.section id={section.id} initial={{
      opacity: 0,
      y: 20
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true
    }} transition={{
      duration: 0.5,
      delay: index * 0.1
    }} className="mb-12 scroll-mt-24" data-id="element-1717">
        <h2 className="text-2xl font-bold text-[#132C53] mb-4 flex items-center space-x-2" data-id="element-1718">
          <span data-id="element-1719">{section.emoji}</span>
          <span data-id="element-1720">
            {section.number} {section.title}
          </span>
        </h2>
        <div className="prose prose-lg max-w-none mb-6" data-id="element-1721">
          <p className="text-gray-600 leading-relaxed" data-id="element-1722">{section.content}</p>
        </div>
        {/* Timeline Steps */}
        <div className="space-y-4" data-id="element-1723">
          <div className="bg-white rounded-lg p-6 shadow-md border-l-4 border-[#357BEB]" data-id="element-1724">
            <div className="flex items-start space-x-4" data-id="element-1725">
              <div className="flex-shrink-0 w-10 h-10 bg-[#357BEB] text-white rounded-full flex items-center justify-center font-bold" data-id="element-1726">
                1
              </div>
              <div className="flex-1" data-id="element-1727">
                <h3 className="font-bold text-[#132C53] mb-2" data-id="element-1728">
                  După semnarea contractului
                </h3>
                <p className="text-gray-600" data-id="element-1729">
                  Agentul trimite datele către facturare.
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg p-6 shadow-md border-l-4 border-[#357BEB]" data-id="element-1730">
            <div className="flex items-start space-x-4" data-id="element-1731">
              <div className="flex-shrink-0 w-10 h-10 bg-[#357BEB] text-white rounded-full flex items-center justify-center font-bold" data-id="element-1732">
                2
              </div>
              <div className="flex-1" data-id="element-1733">
                <h3 className="font-bold text-[#132C53] mb-2" data-id="element-1734">
                  Procesare cerere
                </h3>
                <p className="text-gray-600" data-id="element-1735">
                  Cererea de facturare este soluționată de către departamentul
                  financiar.
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg p-6 shadow-md border-l-4 border-[#357BEB]" data-id="element-1736">
            <div className="flex items-start space-x-4" data-id="element-1737">
              <div className="flex-shrink-0 w-10 h-10 bg-[#357BEB] text-white rounded-full flex items-center justify-center font-bold" data-id="element-1738">
                3
              </div>
              <div className="flex-1" data-id="element-1739">
                <h3 className="font-bold text-[#132C53] mb-2" data-id="element-1740">
                  Emitere factură
                </h3>
                <p className="text-gray-600" data-id="element-1741">
                  Se emite factura către chiriaș – 50% + TVA (21%).
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg p-6 shadow-md border-l-4 border-[#21A366]" data-id="element-1742">
            <div className="flex items-start space-x-4" data-id="element-1743">
              <div className="flex-shrink-0 w-10 h-10 bg-[#21A366] text-white rounded-full flex items-center justify-center font-bold" data-id="element-1744">
                4
              </div>
              <div className="flex-1" data-id="element-1745">
                <h3 className="font-bold text-[#132C53] mb-2" data-id="element-1746">
                  Trimitere și încasare
                </h3>
                <p className="text-gray-600" data-id="element-1747">
                  Factura se trimite prin email și se încasează în contul
                  agenției.
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg p-6 shadow-md border-l-4 border-[#21A366]" data-id="element-1748">
            <div className="flex items-start space-x-4" data-id="element-1749">
              <div className="flex-shrink-0 w-10 h-10 bg-[#21A366] text-white rounded-full flex items-center justify-center font-bold" data-id="element-1750">
                5
              </div>
              <div className="flex-1" data-id="element-1751">
                <h3 className="font-bold text-[#132C53] mb-2" data-id="element-1752">
                  Arhivare tranzacție
                </h3>
                <p className="text-gray-600" data-id="element-1753">
                  După confirmarea plății → tranzacția se arhivează.
                </p>
              </div>
            </div>
          </div>
        </div>
        <div className="mt-6 bg-blue-50 border-l-4 border-[#357BEB] p-6 rounded-r-lg" data-id="element-1754">
          <p className="text-[#132C53] font-medium" data-id="element-1755">
            💬 Niciun comision nu se încasează cash și nici fără factură
            fiscală.
          </p>
        </div>
      </motion.section>;
  }
  if (section.type === 'commission-payment-timing') {
    return <motion.section id={section.id} initial={{
      opacity: 0,
      y: 20
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true
    }} transition={{
      duration: 0.5,
      delay: index * 0.1
    }} className="mb-12 scroll-mt-24" data-id="element-1756">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start" data-id="element-1757">
          <div className="lg:col-span-7" data-id="element-1758">
            <h2 className="text-2xl font-bold text-[#132C53] mb-4 flex items-center space-x-2" data-id="element-1759">
              <span data-id="element-1760">{section.emoji}</span>
              <span data-id="element-1761">
                {section.number} {section.title}
              </span>
            </h2>
            <div className="prose prose-lg max-w-none mb-6" data-id="element-1762">
              <p className="text-gray-600 leading-relaxed mb-6" data-id="element-1763">
                {section.content}
              </p>
            </div>

            {/* Key Information Card */}
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-6 mb-6 border-2 border-blue-200" data-id="element-1764">
              <h3 className="font-bold text-lg text-[#132C53] mb-4" data-id="element-1765">
                Conform contractului:
              </h3>
              <ul className="space-y-3" data-id="element-1766">
                <li className="flex items-start space-x-3" data-id="element-1767">
                  <span className="text-[#357BEB] font-bold mt-1" data-id="element-1768">•</span>
                  <span className="text-gray-700" data-id="element-1769">
                    comisionul este 50% din valoarea primei luni de chirie + TVA
                  </span>
                </li>
                <li className="flex items-start space-x-3" data-id="element-1770">
                  <span className="text-[#357BEB] font-bold mt-1" data-id="element-1771">•</span>
                  <span className="text-gray-700" data-id="element-1772">
                    comisionul devine exigibil doar la semnarea contractului de
                    închiriere între chiriaș și proprietar
                  </span>
                </li>
              </ul>
            </div>

            {/* Important Notice */}
            <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-r-lg mb-6" data-id="element-1773">
              <div className="flex items-start space-x-3" data-id="element-1774">
                <span className="text-2xl" data-id="element-1775">📌</span>
                <div data-id="element-1776">
                  <p className="font-bold text-red-800 mb-2" data-id="element-1777">
                    Chiriaşul NU plăteşte nimic:
                  </p>
                  <ul className="space-y-2 text-gray-700" data-id="element-1778">
                    <li className="flex items-start space-x-2" data-id="element-1779">
                      <span className="text-red-600 mt-1" data-id="element-1780">→</span>
                      <span data-id="element-1781">la semnarea contractului de intermediere</span>
                    </li>
                    <li className="flex items-start space-x-2" data-id="element-1782">
                      <span className="text-red-600 mt-1" data-id="element-1783">→</span>
                      <span data-id="element-1784">la vizionare</span>
                    </li>
                    <li className="flex items-start space-x-2" data-id="element-1785">
                      <span className="text-red-600 mt-1" data-id="element-1786">→</span>
                      <span data-id="element-1787">dacă nu are loc închirierea</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-5" data-id="element-1788">
            <motion.div initial={{
            opacity: 0,
            x: 20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} className="sticky top-24" data-id="element-1789">
              <div className="rounded-2xl overflow-hidden shadow-xl" data-id="element-1790">
                <img src="https://images.unsplash.com/photo-1450101499163-c8848c66ca85?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80" alt={section.imageAlt} className="w-full h-64 object-cover" data-id="element-1791" />
              </div>
              {section.quote && <div className="mt-6 bg-[#357BEB] bg-opacity-10 border-l-4 border-[#357BEB] p-6 rounded-r-xl" data-id="element-1792">
                  <p className="text-[#132C53] font-medium italic" data-id="element-1793">
                    "{section.quote}"
                  </p>
                </div>}
            </motion.div>
          </div>
        </div>
      </motion.section>;
  }
  if (section.type === 'tenant-services-table') {
    return <motion.section id={section.id} initial={{
      opacity: 0,
      y: 20
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true
    }} transition={{
      duration: 0.5,
      delay: index * 0.1
    }} className="mb-12 scroll-mt-24" data-id="element-1794">
        <h2 className="text-2xl font-bold text-[#132C53] mb-4 flex items-center space-x-2" data-id="element-1795">
          <span data-id="element-1796">{section.emoji}</span>
          <span data-id="element-1797">
            {section.number} {section.title}
          </span>
        </h2>
        <div className="prose prose-lg max-w-none mb-6" data-id="element-1798">
          <p className="text-gray-600 leading-relaxed" data-id="element-1799">{section.content}</p>
        </div>
        {/* Services Table */}
        <div className="overflow-x-auto bg-white rounded-lg shadow-md border border-gray-200" data-id="element-1800">
          <table className="min-w-full divide-y divide-gray-200" data-id="element-1801">
            <thead className="bg-gradient-to-r from-[#132C53] to-[#1e4278]" data-id="element-1802">
              <tr data-id="element-1803">
                <th className="px-6 py-4 text-left text-sm font-semibold text-white uppercase tracking-wider" data-id="element-1804">
                  Serviciu inclus
                </th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-white uppercase tracking-wider" data-id="element-1805">
                  Descriere
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-1806">
              <tr className="hover:bg-gray-50" data-id="element-1807">
                <td className="px-6 py-4 text-sm font-medium text-[#132C53]" data-id="element-1808">
                  Consultanță și potrivire
                </td>
                <td className="px-6 py-4 text-sm text-gray-600" data-id="element-1809">
                  Selectarea proprietăților potrivite pentru cererea
                  chiriașului.
                </td>
              </tr>
              <tr className="hover:bg-gray-50" data-id="element-1810">
                <td className="px-6 py-4 text-sm font-medium text-[#132C53]" data-id="element-1811">
                  Programare vizionări
                </td>
                <td className="px-6 py-4 text-sm text-gray-600" data-id="element-1812">
                  Organizarea și coordonarea întâlnirilor cu proprietarii.
                </td>
              </tr>
              <tr className="hover:bg-gray-50" data-id="element-1813">
                <td className="px-6 py-4 text-sm font-medium text-[#132C53]" data-id="element-1814">
                  Negociere
                </td>
                <td className="px-6 py-4 text-sm text-gray-600" data-id="element-1815">
                  Asistență în obținerea celor mai bune condiții.
                </td>
              </tr>
              <tr className="hover:bg-gray-50" data-id="element-1816">
                <td className="px-6 py-4 text-sm font-medium text-[#132C53]" data-id="element-1817">
                  Verificare documente
                </td>
                <td className="px-6 py-4 text-sm text-gray-600" data-id="element-1818">
                  Verificarea contractului și a actelor proprietății.
                </td>
              </tr>
              <tr className="hover:bg-gray-50" data-id="element-1819">
                <td className="px-6 py-4 text-sm font-medium text-[#132C53]" data-id="element-1820">
                  Asistență semnare
                </td>
                <td className="px-6 py-4 text-sm text-gray-600" data-id="element-1821">
                  Prezență la semnarea contractului și închidere tranzacție.
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </motion.section>;
  }
  if (section.type === 'contract-preview') {
    return <motion.section id={section.id} initial={{
      opacity: 0,
      y: 30
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true
    }} transition={{
      duration: 0.6,
      delay: index * 0.1
    }} className="my-12" data-id="element-1822">
        <h2 className="text-2xl md:text-3xl font-bold text-[#132C53] mb-6 flex items-center space-x-3" data-id="element-1823">
          {section.emoji && <span data-id="element-1824">{section.emoji}</span>}
          {section.number && <span data-id="element-1825">{section.number}</span>}
          <span data-id="element-1826">{section.title}</span>
        </h2>
        <div className="prose prose-lg max-w-none mb-8" data-id="element-1827">
          <p className="text-gray-700 leading-relaxed whitespace-pre-line" data-id="element-1828">
            {section.content}
          </p>
        </div>

        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-8 border-2 border-blue-200 shadow-lg" data-id="element-1829">
          <div className="flex flex-col md:flex-row items-center gap-6" data-id="element-1830">
            <div className="flex-shrink-0" data-id="element-1831">
              <div className="w-16 h-16 bg-gradient-to-br from-[#357BEB] to-[#2868d8] rounded-xl flex items-center justify-center shadow-md" data-id="element-1832">
                <FileText className="w-8 h-8 text-white" data-id="element-1833" />
              </div>
            </div>
            <div className="flex-1 text-center md:text-left" data-id="element-1834">
              <h3 className="text-xl font-bold text-[#132C53] mb-2" data-id="element-1835">
                Contract de Închiriere P–C
              </h3>
              <p className="text-gray-600 mb-4" data-id="element-1836">
                Vizualizează modelul oficial de contract de închiriere între
                Proprietar și Chiriaș folosit de agenție.
              </p>
              {section.contractUrl && <a href={section.contractUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-3 px-6 py-3 bg-gradient-to-r from-[#357BEB] to-[#2868d8] text-white font-semibold rounded-lg hover:scale-105 transition-transform shadow-md hover:shadow-xl" data-id="element-1837">
                  <FileText className="w-5 h-5" data-id="element-1838" />
                  Previzualizează Contractul
                  <ExternalLink className="w-4 h-4" data-id="element-1839" />
                </a>}
            </div>
          </div>
        </div>

        <div className="mt-6 bg-amber-50 border-l-4 border-amber-500 p-6 rounded-r-lg" data-id="element-1840">
          <p className="text-amber-900 font-medium" data-id="element-1841">
            💡 <strong data-id="element-1842">Notă:</strong> Contractul se deschide într-o fereastră
            nouă în Google Docs. Poți vizualiza toate clauzele și structura
            documentului.
          </p>
        </div>
      </motion.section>;
  }
  if (section.type === 'contract-preview' && section.contractUrl) {
    return <motion.section id={section.id} initial={{
      opacity: 0,
      y: 30
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true
    }} transition={{
      duration: 0.6,
      delay: index * 0.1
    }} className="my-12" data-id="element-1843">
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-8 border-2 border-blue-200" data-id="element-1844">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6" data-id="element-1845">
            <div className="flex-1" data-id="element-1846">
              <h3 className="text-2xl font-bold text-[#132C53] mb-3" data-id="element-1847">
                {section.title}
              </h3>
              <p className="text-gray-700 leading-relaxed mb-4" data-id="element-1848">
                {section.content}
              </p>
            </div>
            <div className="flex-shrink-0" data-id="element-1849">
              <a href={section.contractUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center space-x-3 bg-gradient-to-r from-[#357BEB] to-[#2868d8] text-white px-8 py-4 rounded-xl font-bold text-lg hover:scale-105 transition-transform shadow-lg hover:shadow-xl" data-id="element-1850">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-1851">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" data-id="element-1852" />
                </svg>
                <span data-id="element-1853">Previzualizează Contract</span>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-1854">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" data-id="element-1855" />
                </svg>
              </a>
            </div>
          </div>
        </div>
      </motion.section>;
  }
  return <motion.section id={section.id} initial={{
    opacity: 0,
    y: 30
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true
  }} transition={{
    duration: 0.6,
    delay: index * 0.1
  }} className="my-12" data-id="element-1856">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start" data-id="element-1857">
        <div className="lg:col-span-7" data-id="element-1858">
          <h2 className="text-2xl md:text-3xl font-bold text-[#132C53] mb-6 flex items-center space-x-3" data-id="element-1859">
            {section.emoji && <span data-id="element-1860">{section.emoji}</span>}
            {section.number && <span data-id="element-1861">{section.number}</span>}
            <span data-id="element-1862">{section.title}</span>
          </h2>
          <div className="prose prose-lg max-w-none" data-id="element-1863">
            {section.content.split('\n\n').map((paragraph, i) => {
            // Handle numbered lists (1. 2. 3. etc.)
            if (/^\d+\./.test(paragraph.trim())) {
              const items = paragraph.split('\n').filter(item => item.trim());
              return <ol key={i} className="space-y-3 mb-6 list-none counter-reset" data-id="element-1864">
                    {items.map((item, j) => {
                  const match = item.match(/^(\d+)\.\s*(.*)/);
                  if (match) {
                    const [, number, text] = match;
                    return <li key={j} className="flex items-start space-x-3 text-gray-700 leading-relaxed" data-id="element-1865">
                            <span className="flex-shrink-0 w-7 h-7 rounded-full bg-[#357BEB] text-white font-bold flex items-center justify-center text-sm" data-id="element-1866">
                              {number}
                            </span>
                            <span className="flex-1 pt-0.5" data-id="element-1867">{text}</span>
                          </li>;
                  }
                  return null;
                })}
                  </ol>;
            }
            // Handle bullet points (•)
            if (paragraph.startsWith('•')) {
              const items = paragraph.split('\n');
              return <ul key={i} className="space-y-3 mb-6 list-none" data-id="element-1868">
                    {items.map((item, j) => <li key={j} className="flex items-start space-x-3 text-gray-700 leading-relaxed" data-id="element-1869">
                        <span className="text-[#357BEB] font-bold mt-1" data-id="element-1870">•</span>
                        <span data-id="element-1871">{item.replace('• ', '')}</span>
                      </li>)}
                  </ul>;
            }
            // Handle bold headings (**text**)
            if (paragraph.includes('**')) {
              const parts = paragraph.split('**');
              return <div key={i} className="mb-4" data-id="element-1872">
                    {parts.map((part, j) => {
                  if (j % 2 === 1) {
                    return <strong key={j} className="text-[#132C53] font-bold text-lg block mb-2" data-id="element-1873">
                            {part}
                          </strong>;
                  }
                  return part ? <span key={j} className="text-gray-700 leading-relaxed" data-id="element-1874">
                          {part}
                        </span> : null;
                })}
                  </div>;
            }
            // Handle arrows (→)
            if (paragraph.includes('→')) {
              return <div key={i} className="flex items-center space-x-3 bg-blue-50 border-l-4 border-[#357BEB] p-4 rounded-r-lg mb-4" data-id="element-1875">
                    <span className="text-gray-700 leading-relaxed font-medium" data-id="element-1876">
                      {paragraph}
                    </span>
                  </div>;
            }
            // Handle emoji indicators (👉)
            if (paragraph.includes('👉')) {
              return <div key={i} className="bg-gradient-to-r from-blue-50 to-indigo-50 border-l-4 border-[#357BEB] p-4 rounded-r-lg mb-4" data-id="element-1877">
                    <p className="text-gray-800 leading-relaxed font-semibold" data-id="element-1878">
                      {paragraph}
                    </p>
                  </div>;
            }
            // Regular paragraphs
            return <p key={i} className="text-gray-700 leading-relaxed mb-4" data-id="element-1879">
                  {paragraph}
                </p>;
          })}
          </div>
        </div>
        <div className="lg:col-span-5" data-id="element-1880">
          {section.image && <motion.div initial={{
          opacity: 0,
          x: 20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="sticky top-24" data-id="element-1881">
              <div className="rounded-2xl overflow-hidden shadow-xl" data-id="element-1882">
                <img src={getSectionImage(section.id, section.title)} alt={section.imageAlt} className="w-full h-64 object-cover" data-id="element-1883" />
              </div>
              {section.quote && <div className="mt-6 bg-[#357BEB] bg-opacity-10 border-l-4 border-[#357BEB] p-6 rounded-r-xl" data-id="element-1884">
                  <p className="text-[#132C53] font-medium italic" data-id="element-1885">
                    "{section.quote}"
                  </p>
                </div>}
            </motion.div>}
        </div>
      </div>
    </motion.section>;
}